import React, { useCallback, useId, useRef, useState } from 'react';
import type { MarketingShot, MarketingShotPanel } from '../../../context/TenantContext';

/** Display ceiling for a capture: never wider than the resolution it was taken
 *  at. A 2x capture of a 390pt phone is 780px of source, so it caps at 390 CSS
 *  px and lands dead-on 2x. Anything above the ceiling is upscaling, which is
 *  blur. See the `.rv-shot` block in app.css. */
const ceiling = (shot: MarketingShot): number => Math.round(shot.width / (shot.density ?? 1));

interface ShotFrameProps {
  shot: MarketingShot;
  /** Hero captures are above the fold and must not be lazy. */
  eager?: boolean;
  className?: string;
}

/** A single capture in its chrome — labelled title bar, hugging frame. */
export const ShotFrame: React.FC<ShotFrameProps> = ({ shot, eager, className }) => (
  <div
    className={`rv-shot${className ? ` ${className}` : ''}`}
    style={{ ['--rv-shot-w' as string]: `${ceiling(shot)}px` }}
  >
    {shot.label && <span className="rv-shot-label">{shot.label}</span>}
    <img
      src={shot.src}
      alt={shot.alt}
      width={shot.width}
      height={shot.height}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
    />
  </div>
);

interface SwitcherProps {
  panels: MarketingShotPanel[];
  /** Accessible name for the tab group. */
  label?: string;
}

/**
 * Tabbed captures. Click-driven only — nothing rotates on its own, because an
 * auto-rotating panel takes the screen away from someone who is still reading
 * it.
 *
 * Every panel stays mounted and is toggled with `hidden`, so the browser has
 * already decoded each image before it is asked for and swapping is instant.
 * That is also why the frame's width ceiling is taken from the WIDEST panel
 * rather than the active one: a per-panel ceiling would resize the frame as
 * you tab through it, and the page would jump under the cursor.
 */
export const Switcher: React.FC<SwitcherProps> = ({ panels, label }) => {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const groupId = useId();

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent, i: number) => {
      const step = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
      if (!step) return;
      e.preventDefault();
      const next = (i + step + panels.length) % panels.length;
      setActive(next);
      tabRefs.current[next]?.focus();
    },
    [panels.length],
  );

  if (panels.length === 0) return null;

  const widest = Math.max(...panels.map(ceiling));
  const current = panels[active];

  return (
    <div className="rv-figure rv-switch">
      <div className="rv-shot" style={{ ['--rv-shot-w' as string]: `${widest}px` }}>
        {current.label && <span className="rv-shot-label">{current.label}</span>}
        {panels.map((p, i) => (
          <img
            key={p.src}
            src={p.src}
            alt={p.alt}
            width={p.width}
            height={p.height}
            hidden={i !== active}
            loading="lazy"
            decoding="async"
          />
        ))}
      </div>

      <div className="rv-tabs" role="group" aria-label={label}>
        {panels.map((p, i) => (
          <button
            key={p.tab}
            type="button"
            ref={(el) => {
              tabRefs.current[i] = el;
            }}
            aria-pressed={i === active}
            aria-controls={`${groupId}-cap`}
            onClick={() => setActive(i)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {p.tab}
          </button>
        ))}
      </div>

      <p className="rv-cap" id={`${groupId}-cap`} aria-live="polite">
        {current.caption}
      </p>
    </div>
  );
};
